import { useReduxState } from 'hooks';
import { User } from 'components/common';
import { Page, PageContent, PageHeader, PageNav } from 'components/page';

import type { NavLink } from 'types';

interface Props {
  activeLink?: string;
  children: React.ReactNode;
  withSidebar?: boolean;
}

const ProfilePage = ({ activeLink = '', children, withSidebar }: Props) => {
  const {
    messages,
    pageState: { user },
    serverConfig
  } = useReduxState();

  const navActiveLink = `/profile${activeLink}`;

  const navLinks: NavLink[] = [
    {
      href: `/profile`,
      icon: 'user',
      title: messages.commonOverview
    },
    ...(serverConfig.hideCoursesFromUI
      ? []
      : [
          {
            href: `/profile/courses`,
            icon: 'book' as const,
            title: messages.commonCourses
          }
        ]),
    ...(serverConfig.hidePathsFromUI
      ? []
      : [
          {
            href: `/profile/paths`,
            icon: 'tasks' as const,
            title: messages.paths
          }
        ]),
    ...(serverConfig.hideAchievementsFromUI
      ? []
      : [
          {
            href: `/profile/achievements`,
            icon: 'check-circle' as const,
            title: messages.commonAchievementsAlt
          }
        ]),
    ...(serverConfig.featureFlags.enableCompetencyImprovements
      ? [
          {
            href: '/profile/competencies',
            icon: 'medal' as const,
            title: messages.commonCompetencies
          }
        ]
      : []),
    ...(serverConfig.platformCompetenciesOnly
      ? []
      : [
          {
            href: `/profile/completions`,
            icon: 'history' as const,
            title: messages.profileCompletionHistory
          }
        ]),
    ...(serverConfig.hideCoursesFromUI
      ? []
      : [
          {
            href: `/profile/feedbacks`,
            icon: 'bullhorn' as const,
            title: messages.profileFeedbacks
          }
        ]),
    {
      href: `/profile/events`,
      icon: 'list',
      title: messages.commonLogs
    },
    {
      alignRight: true,
      href:
        ['local', 'local-totp'].includes(user?.authenticatorType) ||
        serverConfig.authAllowPasswordSetForSsoUser
          ? '/profile/auth'
          : !serverConfig.platformCompetenciesOnly
          ? '/profile/summary-email'
          : '/profile/data',
      icon: 'cog',
      title: messages.commonSettings
    }
  ];

  return (
    <Page activePathname="/profile">
      {!user ? null : (
        <>
          <PageHeader icon="user" title={messages.headerProfile} />

          <PageNav activeLink={navActiveLink} links={navLinks}>
            <User user={user} />
          </PageNav>

          <PageContent withSidebar={withSidebar}>{children}</PageContent>
        </>
      )}
    </Page>
  );
};

export default ProfilePage;
